import type {
  InviteDesign,
  InviteDesignPage,
  InviteCampaign,
  InviteCampaignStats,
  InviteRecipient,
  CreateInviteDesignInput,
  UpdateInviteDesignInput,
  CreateInviteCampaignInput,
  UpdateInviteCampaignInput,
  AddCampaignRecipientsInput,
  UpdateCampaignRecipientInput,
  EventMealOption,
  CreateEventMealOptionInput,
  UpdateEventMealOptionInput,
  GuestPartyMember,
  CreatePartyMemberInput,
  UpdatePartyMemberInput,
  RsvpQuestionDef,
  CreateRsvpQuestionInput,
  UpdateRsvpQuestionInput,
  GuestMessage,
  CreateGuestMessageInput,
  UpdateGuestMessageInput,
  SendGuestMessageResult,
  RemindCampaignResult,
  CheckInResult,
  EventAttendanceSummary,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Designs ────────────────────────────────────────────────────────────────

export async function getInviteDesigns(filters?: {
  eventId?: string;
  isTemplate?: boolean;
}): Promise<InviteDesign[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.isTemplate !== undefined) params.isTemplate = String(filters.isTemplate);
  const { data } = await apiClient.get<InviteDesign[]>('/invites/designs', { params });
  return data;
}

export async function createInviteDesign(input: CreateInviteDesignInput): Promise<InviteDesign> {
  const { data } = await apiClient.post<InviteDesign>('/invites/designs', input);
  return data;
}

export async function updateInviteDesign(
  id: string,
  updates: UpdateInviteDesignInput,
): Promise<InviteDesign> {
  const { data } = await apiClient.patch<InviteDesign>(`/invites/designs/${id}`, updates);
  return data;
}

export async function deleteInviteDesign(id: string): Promise<void> {
  await apiClient.delete(`/invites/designs/${id}`);
}

export async function getDesignArtworkUploadUrl(
  id: string,
  fileName: string,
  contentType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const { data } = await apiClient.post<{ uploadUrl: string; s3Key: string }>(
    `/invites/designs/${id}/artwork/upload-url`,
    { fileName, contentType },
  );
  return data;
}

/**
 * Upload a file to S3 and append it as the next page of the invitation.
 * A single-page invitation is just a design with one page; the backend keeps
 * the design's cover artwork pointed at page 1 for emails and link previews.
 */
export async function uploadDesignPage(id: string, file: File): Promise<InviteDesignPage> {
  const { uploadUrl, s3Key } = await getDesignArtworkUploadUrl(id, file.name, file.type);
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  const { data } = await apiClient.post<InviteDesignPage>(`/invites/designs/${id}/pages`, {
    s3Key,
    mimeType: file.type,
  });
  return data;
}

export async function deleteDesignPage(designId: string, pageId: string): Promise<void> {
  await apiClient.delete(`/invites/designs/${designId}/pages/${pageId}`);
}

/** Upload artwork to S3 and persist the key on the design. */
export async function uploadDesignArtwork(id: string, file: File): Promise<InviteDesign> {
  const { uploadUrl, s3Key } = await getDesignArtworkUploadUrl(id, file.name, file.type);
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  return updateInviteDesign(id, { s3Key, mimeType: file.type });
}

// ─── Campaigns ──────────────────────────────────────────────────────────────

export async function getInviteCampaigns(eventId?: string): Promise<InviteCampaign[]> {
  const params: Record<string, string> = {};
  if (eventId) params.eventId = eventId;
  const { data } = await apiClient.get<InviteCampaign[]>('/invites/campaigns', { params });
  return data;
}

export async function getInviteCampaign(id: string): Promise<InviteCampaign> {
  const { data } = await apiClient.get<InviteCampaign>(`/invites/campaigns/${id}`);
  return data;
}

export async function createInviteCampaign(
  input: CreateInviteCampaignInput,
): Promise<InviteCampaign> {
  const { data } = await apiClient.post<InviteCampaign>('/invites/campaigns', input);
  return data;
}

export async function updateInviteCampaign(
  id: string,
  updates: UpdateInviteCampaignInput,
): Promise<InviteCampaign> {
  const { data } = await apiClient.patch<InviteCampaign>(`/invites/campaigns/${id}`, updates);
  return data;
}

export async function deleteInviteCampaign(id: string): Promise<void> {
  await apiClient.delete(`/invites/campaigns/${id}`);
}

export async function getCampaignRecipients(
  campaignId: string,
  status?: string,
): Promise<InviteRecipient[]> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const { data } = await apiClient.get<InviteRecipient[]>(
    `/invites/campaigns/${campaignId}/recipients`,
    { params },
  );
  return data;
}

export async function addCampaignRecipients(
  campaignId: string,
  input: AddCampaignRecipientsInput,
): Promise<{ added: number; recipients: InviteRecipient[] }> {
  const { data } = await apiClient.post<{ added: number; recipients: InviteRecipient[] }>(
    `/invites/campaigns/${campaignId}/recipients`,
    input,
  );
  return data;
}

/** Producer override of one recipient's RSVP answer and/or delivery status. */
export async function updateCampaignRecipient(
  campaignId: string,
  recipientId: string,
  updates: UpdateCampaignRecipientInput,
): Promise<InviteRecipient> {
  const { data } = await apiClient.patch<InviteRecipient>(
    `/invites/campaigns/${campaignId}/recipients/${recipientId}`,
    updates,
  );
  return data;
}

export async function removeCampaignRecipient(
  campaignId: string,
  recipientId: string,
): Promise<void> {
  await apiClient.delete(`/invites/campaigns/${campaignId}/recipients/${recipientId}`);
}

export interface SendCampaignResult {
  /** Parties reached (recipients marked SENT). */
  sent: number;
  failed: number;
  skipped: number;
  /** Individual emails delivered — a couple counts twice. */
  emailsSent: number;
  campaign: InviteCampaign;
}

export async function sendInviteCampaign(campaignId: string): Promise<SendCampaignResult> {
  const { data } = await apiClient.post<SendCampaignResult>(
    `/invites/campaigns/${campaignId}/send`,
  );
  return data;
}

/**
 * Generate (or revoke) the campaign's guest-less public share link — the one
 * the host forwards to people who are not on the guest list.
 */
export async function setCampaignPublicLink(
  campaignId: string,
  enabled: boolean,
): Promise<InviteCampaign> {
  const { data } = await apiClient.post<InviteCampaign>(
    `/invites/campaigns/${campaignId}/public-link`,
    { enabled },
  );
  return data;
}

export async function getInviteCampaignStats(campaignId: string): Promise<InviteCampaignStats> {
  const { data } = await apiClient.get<InviteCampaignStats>(
    `/invites/campaigns/${campaignId}/stats`,
  );
  return data;
}

// ─── Phase 2: sub-events, meal options, party members, questions ────────────

export interface SubEvent {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
}

export async function getSubEvents(eventId: string): Promise<SubEvent[]> {
  const { data } = await apiClient.get<SubEvent[]>(`/invites/events/${eventId}/sub-events`);
  return data;
}

export async function getMealOptions(eventId: string): Promise<EventMealOption[]> {
  const { data } = await apiClient.get<EventMealOption[]>(
    `/invites/events/${eventId}/meal-options`,
  );
  return data;
}

export async function createMealOption(
  eventId: string,
  input: CreateEventMealOptionInput,
): Promise<EventMealOption> {
  const { data } = await apiClient.post<EventMealOption>(
    `/invites/events/${eventId}/meal-options`,
    input,
  );
  return data;
}

export async function updateMealOption(
  eventId: string,
  optionId: string,
  updates: UpdateEventMealOptionInput,
): Promise<EventMealOption> {
  const { data } = await apiClient.patch<EventMealOption>(
    `/invites/events/${eventId}/meal-options/${optionId}`,
    updates,
  );
  return data;
}

export async function deleteMealOption(eventId: string, optionId: string): Promise<void> {
  await apiClient.delete(`/invites/events/${eventId}/meal-options/${optionId}`);
}

export async function getPartyMembers(guestId: string): Promise<GuestPartyMember[]> {
  const { data } = await apiClient.get<GuestPartyMember[]>(
    `/invites/guests/${guestId}/party-members`,
  );
  return data;
}

export async function createPartyMember(
  guestId: string,
  input: CreatePartyMemberInput,
): Promise<GuestPartyMember> {
  const { data } = await apiClient.post<GuestPartyMember>(
    `/invites/guests/${guestId}/party-members`,
    input,
  );
  return data;
}

export async function updatePartyMember(
  guestId: string,
  memberId: string,
  updates: UpdatePartyMemberInput,
): Promise<GuestPartyMember> {
  const { data } = await apiClient.patch<GuestPartyMember>(
    `/invites/guests/${guestId}/party-members/${memberId}`,
    updates,
  );
  return data;
}

export async function deletePartyMember(guestId: string, memberId: string): Promise<void> {
  await apiClient.delete(`/invites/guests/${guestId}/party-members/${memberId}`);
}

export async function getCampaignQuestions(campaignId: string): Promise<RsvpQuestionDef[]> {
  const { data } = await apiClient.get<RsvpQuestionDef[]>(
    `/invites/campaigns/${campaignId}/questions`,
  );
  return data;
}

export async function addCampaignQuestion(
  campaignId: string,
  input: CreateRsvpQuestionInput,
): Promise<RsvpQuestionDef> {
  const { data } = await apiClient.post<RsvpQuestionDef>(
    `/invites/campaigns/${campaignId}/questions`,
    input,
  );
  return data;
}

export async function updateCampaignQuestion(
  questionId: string,
  updates: UpdateRsvpQuestionInput,
): Promise<RsvpQuestionDef> {
  const { data } = await apiClient.patch<RsvpQuestionDef>(
    `/invites/questions/${questionId}`,
    updates,
  );
  return data;
}

export async function deleteCampaignQuestion(questionId: string): Promise<void> {
  await apiClient.delete(`/invites/questions/${questionId}`);
}

// ─── Phase 3: reminders, scheduling, guest messages ─────────────────────────

export async function remindInviteCampaign(campaignId: string): Promise<RemindCampaignResult> {
  const { data } = await apiClient.post<RemindCampaignResult>(
    `/invites/campaigns/${campaignId}/remind`,
  );
  return data;
}

export async function scheduleInviteCampaign(
  campaignId: string,
  scheduledAt: string | null,
): Promise<InviteCampaign> {
  const { data } = await apiClient.post<InviteCampaign>(
    `/invites/campaigns/${campaignId}/schedule`,
    { scheduledAt },
  );
  return data;
}

export async function getGuestMessages(eventId?: string): Promise<GuestMessage[]> {
  const params: Record<string, string> = {};
  if (eventId) params.eventId = eventId;
  const { data } = await apiClient.get<GuestMessage[]>('/invites/messages', { params });
  return data;
}

export async function createGuestMessage(input: CreateGuestMessageInput): Promise<GuestMessage> {
  const { data } = await apiClient.post<GuestMessage>('/invites/messages', input);
  return data;
}

export async function updateGuestMessage(
  id: string,
  updates: UpdateGuestMessageInput,
): Promise<GuestMessage> {
  const { data } = await apiClient.patch<GuestMessage>(`/invites/messages/${id}`, updates);
  return data;
}

export async function deleteGuestMessage(id: string): Promise<void> {
  await apiClient.delete(`/invites/messages/${id}`);
}

export async function sendGuestMessage(id: string): Promise<SendGuestMessageResult> {
  const { data } = await apiClient.post<SendGuestMessageResult>(`/invites/messages/${id}/send`);
  return data;
}

// ─── Phase 4: check-in & attendance ─────────────────────────────────────────

export async function checkInByToken(
  token: string,
  undo?: boolean,
  eventId?: string,
): Promise<CheckInResult> {
  const { data } = await apiClient.post<CheckInResult>(
    `/invites/checkin/${encodeURIComponent(token)}`,
    { ...(undo ? { undo } : {}), ...(eventId ? { eventId } : {}) },
  );
  return data;
}

export async function checkInGuest(
  guestId: string,
  undo?: boolean,
  eventId?: string,
): Promise<CheckInResult> {
  const { data } = await apiClient.post<CheckInResult>(`/invites/guests/${guestId}/checkin`, {
    ...(undo ? { undo } : {}),
    ...(eventId ? { eventId } : {}),
  });
  return data;
}

export async function getEventAttendance(eventId: string): Promise<EventAttendanceSummary> {
  const { data } = await apiClient.get<EventAttendanceSummary>(
    `/invites/events/${eventId}/attendance`,
  );
  return data;
}
